package com.mps.asc_app.data.functions

import com.mps.asc_app.data.auth.AuthRepository
import com.mps.asc_app.data.places.model.AddressSuggestion
import com.mps.asc_app.data.places.model.PlaceDetailsResponse
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.functions.functions
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.jsonObject

class FunctionsRepositoryImpl(
    private val httpClient: HttpClient,
    private val authRepository: AuthRepository,
    private val json: Json = Json { ignoreUnknownKeys = true; isLenient = true }
) : FunctionsRepository {

    private val functions = Firebase.functions(region = "europe-west1")

    override suspend fun verifyAndSaveUserProfile(
        uid: String,
        firstName: String,
        lastName: String,
        email: String,
        placeId: String
    ): Result<Unit> = runCatching {
        functions
            .httpsCallable("verifyAndSaveUserProfile")(
                VerifyAndSaveUserProfilePayload(
                    uid = uid,
                    firstName = firstName,
                    lastName = lastName,
                    email = email,
                    placeId = placeId
                )
            )
    }.map { }

    override suspend fun placesAutocomplete(
        input: String
    ): Result<List<AddressSuggestion>> = runCatching {
        val result: List<AddressSuggestion> = functions
            .httpsCallable("placesAutocomplete")(
                AutocompletePayload(input = input)
            )
            .data()

        result
    }

    override suspend fun placeDetails(
        placeId: String
    ): Result<PlaceDetailsResponse> = runCatching {
        val result: PlaceDetailsResponse = functions
            .httpsCallable("placeDetails")(
                PlaceDetailsPayload(placeId = placeId)
            )
            .data()

        result
    }

    override suspend fun getEmailVerifiedFlags(
        uids: List<String>
    ): Result<Map<String, Boolean>> = runCatching {
        val cleaned = uids
            .map { it.trim() }
            .filter { it.isNotBlank() }
            .distinct()

        if (cleaned.isEmpty()) {
            return@runCatching emptyMap()
        }

        val result: EmailVerifiedFlagsResponse = functions
            .httpsCallable("getEmailVerifiedFlags")(
                EmailVerifiedFlagsPayload(uids = cleaned)
            )
            .data()

        result.map
    }

    override suspend fun resendEmailVerification(uid: String): Result<Unit> = runCatching {
        // WICHTIG: adminResendEmailVerificationHttp ist eine HTTP onRequest Function (keine Callable Function).
        // Daher NICHT über Firebase.functions.httpsCallable aufrufen, sonst kommt: "Response is missing data field".

        val token = authRepository.getIdToken()
            ?: error("Nicht eingeloggt.")

        val url = "https://europe-west1-asc-cranz-estebruegge.cloudfunctions.net/adminResendEmailVerificationHttp"

        val resp = httpClient.post(url) {
            contentType(ContentType.Application.Json)
            header(HttpHeaders.Authorization, "Bearer $token")
            setBody(ResendEmailVerificationPayload(uid = uid))
        }

        val bodyText: String = runCatching { resp.body<String>() }.getOrElse { "" }

        // Bei Fehlerstatus: versuche Message aus JSON zu extrahieren.
        if (!resp.status.isSuccess()) {
            val msg = extractHttpErrorMessage(bodyText) ?: bodyText.ifBlank { "HTTP ${resp.status.value}" }
            error(msg)
        }

        // Auch bei 200 kann { error: { ... } } zurückkommen.
        val errorMessage = extractHttpErrorMessage(bodyText)
        if (!errorMessage.isNullOrBlank()) {
            error(errorMessage)
        }
    }

    override suspend fun adminDeleteUser(uid: String): Result<Unit> = runCatching {
        val cleanedUid = uid.trim()
        require(cleanedUid.isNotBlank()) { "uid required" }

        // 1) Falls die Function als Callable existiert, nutzen wir sie bevorzugt.
        // Wenn sie aber als HTTP onRequest implementiert ist, kommt typischerweise
        // "Response is missing data field" – dann fällt der Code auf HTTP zurück.
        val callableAttempt = runCatching {
            functions
                .httpsCallable("adminDeleteUser")(
                    DeleteUserPayload(uid = cleanedUid)
                )
        }

        if (callableAttempt.isSuccess) return@runCatching

        val callableError = callableAttempt.exceptionOrNull()
        val callableMsg = callableError?.message.orEmpty()
        val shouldHttpFallback =
            callableMsg.contains("Response is missing data field", ignoreCase = true) ||
                callableMsg.contains("not found", ignoreCase = true)

        // If callable endpoint exists and returned a real business/auth error,
        // surface that directly instead of masking it with HTTP fallback errors.
        if (!shouldHttpFallback) {
            throw (callableError ?: IllegalStateException("User konnte nicht gelöscht werden."))
        }

        // 2) HTTP-Fallback (Gen2 Functions haben oft beide Endpoints).
        val token = authRepository.getIdToken() ?: error("Nicht eingeloggt.")

        val candidateUrls = listOf(
            "https://europe-west1-asc-cranz-estebruegge.cloudfunctions.net/adminDeleteUser",
            "https://europe-west1-asc-cranz-estebruegge.cloudfunctions.net/adminDeleteUserHttp",
        )

        var lastError: Throwable? = callableError
        for (url in candidateUrls) {
            val resp = runCatching {
                httpClient.post(url) {
                    contentType(ContentType.Application.Json)
                    header(HttpHeaders.Authorization, "Bearer $token")
                    setBody(DeleteUserPayload(uid = cleanedUid))
                }
            }.getOrElse {
                lastError = it
                continue
            }

            val bodyText: String = runCatching { resp.body<String>() }.getOrElse { "" }

            if (!resp.status.isSuccess()) {
                val msg = extractHttpErrorMessage(bodyText) ?: bodyText.ifBlank { "HTTP ${resp.status.value}" }
                lastError = IllegalStateException(msg)
                continue
            }

            val errorMessage = extractHttpErrorMessage(bodyText)
            if (!errorMessage.isNullOrBlank()) {
                lastError = IllegalStateException(errorMessage)
                continue
            }

            // Success
            return@runCatching
        }

        throw (lastError ?: IllegalStateException("User konnte nicht gelöscht werden."))
    }

    override suspend fun adminTriggerPendingAdminsNotification(uid: String): Result<Unit> = runCatching {
        val cleanedUid = uid.trim()
        require(cleanedUid.isNotBlank()) { "uid required" }

        functions
            .httpsCallable("adminTriggerPendingAdminsNotification")(
                TriggerPendingAdminsPayload(uid = cleanedUid)
            )
    }.map { }

    override suspend fun adminBackfillBookingPlayerSnapshots(
        limit: Int,
        dryRun: Boolean,
    ): Result<Unit> = runCatching {
        functions
            .httpsCallable("adminBackfillBookingPlayerSnapshots")(
                BackfillBookingPlayersPayload(
                    limit = limit,
                    dryRun = dryRun,
                )
            )
    }.map { }

    override suspend fun adminRemoveLegacyUserStatusField(): Result<Unit> = runCatching {
        functions
            .httpsCallable("adminRemoveLegacyUserStatusField")(
                emptyMap<String, String>()
            )
    }.map { }

    override suspend fun ensureDebugTestUserExists(): Result<Unit> = runCatching {
        functions
            .httpsCallable("ensureDebugTestUserExists")(
                emptyMap<String, String>()
            )
    }.map { }

    override suspend fun ensureDebugTestUserHasMemberNumberExists(): Result<Unit> = runCatching {
        functions
            .httpsCallable("ensureDebugTestUserHasMemberNumberExists")(
                emptyMap<String, String>()
            )
    }.map { }

    private fun extractHttpErrorMessage(bodyText: String): String? {
        if (bodyText.isBlank()) return null

        val root = runCatching { json.parseToJsonElement(bodyText) }.getOrNull() as? JsonObject ?: return null

        // Erwartete Form: { error: { status: "...", message: "..." } }
        val errObj = root["error"]?.jsonObject ?: return null
        return errObj["message"]?.toString()?.trim('"')
            ?.takeIf { it.isNotBlank() }
    }
}

@Serializable
private data class ResendEmailVerificationPayload(
    val uid: String
)

@Serializable
private data class DeleteUserPayload(
    val uid: String
)

@Serializable
private data class TriggerPendingAdminsPayload(
    val uid: String
)

@Serializable
private data class BackfillBookingPlayersPayload(
    val limit: Int,
    val dryRun: Boolean,
)

@Serializable
private data class VerifyAndSaveUserProfilePayload(
    val uid: String,
    val firstName: String,
    val lastName: String,
    val email: String,
    val placeId: String
)

@Serializable
private data class AutocompletePayload(
    val input: String
)

@Serializable
private data class PlaceDetailsPayload(
    val placeId: String
)

@Serializable
private data class EmailVerifiedFlagsPayload(
    val uids: List<String>
)

@Serializable
private data class EmailVerifiedFlagsResponse(
    val map: Map<String, Boolean> = emptyMap()
)
